import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { CustomTableReport } from 'src/app/interfaces/custom-table-report/custom-table-report.interface';
import { InventoryReportFilterModel } from 'src/app/models/inventory/inventory-report-filter.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { GroupService } from 'src/app/services/archive/group.service';
import { SubgroupService } from 'src/app/services/archive/subgroup.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { InventoryPeriodService } from 'src/app/services/inventory/inventory-period.service';
import { InventoryReportService } from 'src/app/services/inventory/inventory-report.service';
import { InventoryService } from 'src/app/services/inventory/inventory.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { CustomValidators } from 'src/app/validators/custom-validators';

@Component({
  selector: 'app-type_detailed_values',
  templateUrl: './type_detailed_values.component.html',
  styleUrls: ['./type_detailed_values.component.scss'],
  standalone: false
})

export class TypeDetailedValuesComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('filtersContainer') filtersContainer!: ElementRef;

  changeFilters: boolean = false;
  dataReport: CustomTableReport;
  filterObserver?: ResizeObserver;
  filtersHeight: number = 0;
  filteredSubgroups: any[] = [];
  formInventoryReportFilter: FormGroup = new FormGroup({});
  listArticle: any[] = [];
  listGroup: any[] = [];
  listInventory: any[] = [];
  listSubGroup: any[] = [];
  nameWindow: string = 'Informe de Inventario por Tipo-Valores Detallado';
  projectName = ProjectName.projectName;

  constructor(private articleService: ArticleService,
    private formBuilder: FormBuilder,
    private groupService: GroupService,
    private headerService: HeaderService,
    private inventoryPeriodService: InventoryPeriodService,
    private inventoryReportService: InventoryReportService,
    private inventoryService: InventoryService,
    private subgroupService: SubgroupService,
    private title: Title,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm();
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    await this.getAllLists();
    this.getLastInventoryPeriod();
  }

  ngAfterViewInit() {
    this.filterObserver = new ResizeObserver(() => {
      this.filtersHeight = this.filtersContainer?.nativeElement?.offsetHeight || 0;
    });
    this.filterObserver.observe(this.filtersContainer.nativeElement);
  }

  ngOnDestroy() {
    this.filterObserver?.disconnect();
  }

  changeArticle(articleId: number | null) {
    if (articleId === 0 || articleId === null) {
      this.formInventoryReportFilter.controls["articleId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeFilter() {
    this.changeFilters = false;
  }

  changeGroup(groupId: number | null) {
    this.filterSubgroupByGroupId(groupId);
    if (groupId != 0 && groupId != null) {
      this.formInventoryReportFilter.controls["subgroupId"].patchValue(0);
    } else {
      this.formInventoryReportFilter.controls["subgroupId"].patchValue(0);
      this.formInventoryReportFilter.controls["groupId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeInventory(inventoryId: number | null) {
    if (inventoryId === 0 || inventoryId === null) {
      this.formInventoryReportFilter.controls["inventoryId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeSubgroup(subgroupId: number | null) {
    if (subgroupId != 0 && subgroupId != null) {
      const groupId = this.listSubGroup.find(x => x.value === subgroupId)!.groupId;
      this.formInventoryReportFilter.controls["groupId"].patchValue(groupId);
      this.filterSubgroupByGroupId(groupId);
    } else {
      this.formInventoryReportFilter.controls["subgroupId"].patchValue(0);
    }
    this.changeFilter();
  }

  clear() {
    this.changeFilter();
    this.validateForm();
    this.getLastInventoryPeriod();
  }

  async exportExcel() {
    if (this.changeFilters) {
      const dateInitial = this.formInventoryReportFilter.controls['dateInitial'].value;
      const initialDate = `${dateInitial.getFullYear()}-${(dateInitial.getMonth() + 1).toString().padStart(2, '0')}`;
      const period = initialDate.slice(5);
      const year = initialDate.slice(0, -3);

      const modelInventoryReportFilter = this.formInventoryReportFilter.value as InventoryReportFilterModel;
      modelInventoryReportFilter.year = parseInt(year);
      modelInventoryReportFilter.period = parseInt(period);
      try {
        const response = await firstValueFrom(this.inventoryReportService.exportInventoryReportTypeDetailedValues(modelInventoryReportFilter));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Informe de Inventario por Tipo-Valores Detallado.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar el informe.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para exportar.'
      );
    }
  }

  filterSubgroupByGroupId(groupId: number | null) {
    const rowsFiltered = this.listSubGroup.filter(x => x.groupId === groupId);
    if (groupId != null) {
      this.filteredSubgroups = rowsFiltered;
      const subgroupId = this.formInventoryReportFilter.get("subgroupId")?.value;
      const subgroupExists = this.listSubGroup.some(x => x.groupId === groupId && x.value === subgroupId);

      if (subgroupId && !subgroupExists) {
        this.formInventoryReportFilter.controls["subgroupId"].patchValue(0);
      }
    } else {
      this.filteredSubgroups = this.listSubGroup;
    }
  }

  async getAllLists() {
    try {
      const [articles, inventories, groups, subgroups] = await Promise.all([
        firstValueFrom(this.articleService.getAllArticlesForSelectAsync()),
        firstValueFrom(this.inventoryService.getAllInventoriesForSelectAsync()),
        firstValueFrom(this.groupService.getAllGroupsForSelectAsync()),
        firstValueFrom(this.subgroupService.getAllSubgroupsForSelectAsync())
      ]);
      this.listArticle = articles;
      this.listInventory = inventories;
      this.listGroup = groups;
      this.listSubGroup = subgroups;
      this.filteredSubgroups = this.listSubGroup;
      return true;
    } catch (error) {
      console.error('Error al obtener las listas:', error);
      return false;
    }
  }

  async getLastInventoryPeriod() {
    try {
      const rentalPeriod = await firstValueFrom(this.inventoryPeriodService.getLastInventoryPeriod());
      this.formInventoryReportFilter.controls["dateInitial"].patchValue(moment(rentalPeriod.initialDate).startOf('month').toDate());
      this.filteredSubgroups = this.listSubGroup;
    } catch (error) {
      console.error("Error al consultar el periodo de inventario:", error);
    }
  }

  printReport() {
    if (this.changeFilters) {
      window.print();
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para imprimir.'
      );
    }
  }

  async send() {
    Object.keys(this.formInventoryReportFilter.controls).forEach(key => {
      const control = this.formInventoryReportFilter.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formInventoryReportFilter.valid) {
      const dateInitial = this.formInventoryReportFilter.controls['dateInitial'].value;
      const initialDate = `${dateInitial.getFullYear()}-${(dateInitial.getMonth() + 1).toString().padStart(2, '0')}`;
      const period = initialDate.slice(5);
      const year = initialDate.slice(0, -3);

      const modelInventoryReportFilter = this.formInventoryReportFilter.value as InventoryReportFilterModel;
      modelInventoryReportFilter.year = parseInt(year);
      modelInventoryReportFilter.period = parseInt(period);

      try {
        this.dataReport = await firstValueFrom(this.inventoryReportService.inventoryReportTypeDetailedValues(modelInventoryReportFilter));
        if (this.dataReport.data && this.dataReport.data.length != 0 && this.dataReport.data.length != null) {
          this.changeFilters = true;
        } else {
          this.changeFilters = false;
          this.toastHandlerService.showToast('info', 'No se encontraron datos.');
        }
      } catch (error) {
        console.error("Error al consultar el informe:", error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      dateInitial: {
        required: 'El campo Período Inicial es obligatorio.'
      },
      inventoryId: {
        required: 'El campo Inventario es obligatorio.',
        min: 'Debe seleccionar un Inventario válido.'
      },
      groupId: {
        notNumber: 'Debe seleccionar un Grupo válido.'
      },
      subgroupId: {
        notNumber: 'Debe seleccionar un Subgrupo válido.'
      },
      articleId: {
        notNumber: 'Debe seleccionar un Artículo válido.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formInventoryReportFilter.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm() {
    this.formInventoryReportFilter = this.formBuilder.group({
      dateInitial: ['', [Validators.required]],
      inventoryId: [0, [Validators.required, Validators.min(1)]],
      groupId: [0, [CustomValidators.numberOnlyValidator()]],
      subgroupId: [0, [CustomValidators.numberOnlyValidator()]],
      articleId: [0, [CustomValidators.numberOnlyValidator()]]
    });
  }

}
